import * as agendaCobranza from "./agendaCobranza";
import { toJsDate, toFechaSoloLocal } from "./fechas";

const clamp = (num, min, max) => Math.min(Math.max(Number(num) || 0, min), max);

// Acepta Timestamp, Date, string ISO, {seconds}, número ms.
// Crítico: después del restore desde prod los Timestamps llegan como strings ISO,
// así que NO podemos confiar en .toDate(). Esto causaba que el motor descartara
// todos los pagos y reportara "sin_historial" inflando saldoAtrasado.
const toDate = (value) => toJsDate(value);

const diasEntre = (a, b = new Date()) => {
  if (!a) return 9999;
  return Math.floor((agendaCobranza.startOfDay(b) - agendaCobranza.startOfDay(a)) / 86400000);
};

export const isDeudaCobrable = (data = {}) => {
  const estado = String(data?.estado || "").toLowerCase();
  const estatus = String(data?.estatus || "").toLowerCase();

  if (data?.activo === false) return false;
  if (data?.devolucion === true) return false;
  if (estado.includes("devolucion")) return false;
  if (estatus.includes("cancel")) return false;
  if (estatus.includes("cerrad")) return false;

  const saldo = agendaCobranza.calcularSaldoMostrable(data);
  if (saldo <= 0) return false;

  return true;
};


const getPagosPositivos = (data = {}) =>
  Array.isArray(data.pagos)
    ? data.pagos
        .filter((p) => Number(p?.cantidad || 0) > 0)
        .map((p) => ({
          ...p,
          cantidad: Number(p.cantidad || 0),
          fechaDate: toDate(p.fecha),
        }))
        .filter((p) => p.fechaDate)
    : [];

const isPagoEnganche = (p = {}) => {
  const tipo = String(p?.tipo || "").toLowerCase();
  const metodo = String(p?.metodo || "").toLowerCase();
  const concepto = String(p?.concepto || "").toLowerCase();

  return (
    tipo.includes("enganche") ||
    metodo.includes("enganche") ||
    concepto.includes("enganche")
  );
};

const getPagosOrdinariosPositivos = (data = {}) =>
  getPagosPositivos(data).filter((p) => !isPagoEnganche(p));

const getPlazoMeses = (data = {}) => {
  const plazo = agendaCobranza.toNumber(
    data?.plazo ?? data?.plazoMeses ?? data?.meses
  );

  return plazo > 0 ? plazo : 0;
};

export const getFechaInicioReal = (data = {}) => {
  // FECHA VERIFICADA (autoritativa): si un admin marcó la fecha de inicio como
  // verificada, esa fecha contractual MANDA como ancla y NO se deja sobrescribir
  // por el mínimo de pagos/creación. El mínimo-de-fechas es el comportamiento
  // defensivo por DEFECTO (para deudas con fecha de inicio no confiable), pero
  // una vez verificada por un humano debe respetarse tal cual.
  // Backward-compatible: sin el flag, se conserva el cálculo por mínimo de siempre.
  if (data?.fechaInicioVerificada) {
    const dv =
      toFechaSoloLocal(data?.fechaInicio) ||
      toFechaSoloLocal(data?.fechaInicioDeuda) ||
      toFechaSoloLocal(data?.fechaContrato) ||
      toFechaSoloLocal(data?.fechaVenta);
    if (dv) return dv;
  }

  const fechasCandidatas = [];

  const pushFecha = (valor) => {
    // toFechaSoloLocal normaliza fechas legacy UTC midnight a local midnight
    // del mismo dia visible, evitando que getFechaInicioReal se corra un dia.
    const d = toFechaSoloLocal(valor);
    if (d) fechasCandidatas.push(d);
  };

  // Fecha configurada / contractual
  pushFecha(data?.fechaInicio);
  pushFecha(data?.fechaInicioDeuda);
  pushFecha(data?.fechaContrato);
  pushFecha(data?.fechaVenta);

  // Primer pago real registrado
  const pagos = getPagosPositivos(data);
  if (pagos.length > 0) {
    const primerPago = new Date(
      Math.min(...pagos.map((p) => p.fechaDate.getTime()))
    );

    pushFecha(primerPago);
  }

  // Fallbacks legacy
  pushFecha(data?.fechaEnganche);
  pushFecha(data?.fechaCreacion);
  pushFecha(data?.creada);
  pushFecha(data?.fecha);

  if (fechasCandidatas.length === 0) return new Date();

  return new Date(
    Math.min(...fechasCandidatas.map((d) => d.getTime()))
  );
};

export const getMesesTranscurridosReales = (data = {}, baseDate = new Date()) => {
  const inicio = getFechaInicioReal(data);
  if (!inicio) return 0;

  const hoy = new Date(baseDate);

  let meses =
    (hoy.getFullYear() - inicio.getFullYear()) * 12 +
    (hoy.getMonth() - inicio.getMonth());

  // 🔥 AJUSTE POR DÍA (clave)
  if (hoy.getDate() < inicio.getDate()) {
    meses -= 1;
  }

  return Math.max(0, meses);
};

export const getMesesVencidosParaAtraso = (data = {}, baseDate = new Date()) => {
  const mesesTranscurridos = getMesesTranscurridosReales(data, baseDate);
  const plazoMeses = getPlazoMeses(data);

  return plazoMeses > 0 ? Math.min(mesesTranscurridos, plazoMeses) : mesesTranscurridos;
};

export const getTotalPagadoReal = (data = {}) => {
  const pagos = getPagosPositivos(data);

  const totalPagos = pagos.reduce((acc, p) => acc + p.cantidad, 0);

  const enganche = agendaCobranza.toNumber(data?.enganche);

  const hayEngancheEnPagos = pagos.some((p) => {
    const tipo = String(p?.tipo || "").toLowerCase();
    const metodo = String(p?.metodo || "").toLowerCase();
    const concepto = String(p?.concepto || "").toLowerCase();

    return (
      tipo.includes("enganche") ||
      metodo.includes("enganche") ||
      concepto.includes("enganche")
    );
  });

  return hayEngancheEnPagos ? totalPagos : totalPagos + enganche;
};

export const getTotalPagadoOrdinarioReal = (data = {}) => {
  const pagos = getPagosOrdinariosPositivos(data);
  return pagos.reduce((acc, p) => acc + p.cantidad, 0);
};

export const getSaldoAtrasadoReal = (data = {}, baseDate = new Date()) => {
  const mensualidad = agendaCobranza.toNumber(
    data?.mensualidad ?? data?.pagoMensual
  );

  if (!mensualidad || mensualidad <= 0) return 0;

  const saldoRestante = agendaCobranza.calcularSaldoMostrable(data);

  const mesesTranscurridos = getMesesVencidosParaAtraso(data, baseDate);
  const vencidoTeorico = mesesTranscurridos * mensualidad;
  const totalPagado = getTotalPagadoOrdinarioReal(data);
  const atrasoTeorico = Math.max(vencidoTeorico - totalPagado, 0);

  // PDF-Bug6 (refinado): si la cuenta está fuera de plazo Y los datos son
  // consistentes (saldoRestante ≤ esquema teórico × 1.05), retornamos el
  // saldoRestante completo para evitar la diferencia fantasma por redondeo.
  //
  // Si hay INCONSISTENCIA de datos (saldoRestante mucho mayor que el esquema
  // mensualidades × plazo + enganche, ej. balloon payment no registrado), NO
  // inflamos artificialmente — mantenemos el cálculo teórico para no acusar
  // al cliente de atraso por dinero que su esquema original quizá no exigía.
  const mesesRealesAtraso = getMesesTranscurridosReales(data, baseDate);
  const plazoAtraso = getPlazoMeses(data);
  if (plazoAtraso > 0 && mesesRealesAtraso >= plazoAtraso) {
    const enganche = agendaCobranza.toNumber(data?.enganche);
    const esperadoMaximoEsquema = plazoAtraso * mensualidad + enganche;
    const montoTotalDeuda = agendaCobranza.toNumber(data?.monto ?? data?.importe);
    // Datos consistentes: el saldo cabe dentro del esquema teórico (con 5%
    // de tolerancia para redondeos).
    if (montoTotalDeuda <= esperadoMaximoEsquema * 1.05) {
      return Math.round(saldoRestante * 100) / 100;
    }
    // Caso con esquema atípico (balloon, enganche oculto, etc.):
    // reportamos el atraso teórico (sin inflar) y dejamos que el saldoRestante
    // se vea solo como "lo que queda por pagar", no necesariamente atrasado.
    return Math.min(atrasoTeorico, saldoRestante);
  }

  return Math.min(atrasoTeorico, saldoRestante);
};

export const getMesesAtrasoFinanciero = (data = {}, baseDate = new Date()) => {
  const mensualidad = agendaCobranza.getMensualidadMostrable(data);
  const saldoAtrasado = getSaldoAtrasadoReal(data, baseDate);

  if (mensualidad <= 0) return 0;

  return Math.floor(saldoAtrasado / mensualidad);
};

export const getDiasSinPago = (data = {}, baseDate = new Date()) => {
  const pagos = getPagosOrdinariosPositivos(data);
  if (pagos.length === 0) return 9999;

  const ultimoPago = new Date(Math.max(...pagos.map((p) => p.fechaDate.getTime())));
  return diasEntre(ultimoPago, baseDate);
};

export const getPagosUltimosMeses = (data = {}, meses = 6, baseDate = new Date()) => {
  const pagos = getPagosOrdinariosPositivos(data);
  const hoy = new Date(baseDate);
  const limite = new Date(hoy);
  limite.setMonth(limite.getMonth() - meses);

  return pagos.filter((p) => p.fechaDate >= limite);
};

export const detectarPatronPago = (data = {}, baseDate = new Date()) => {
  const mensualidad = agendaCobranza.getMensualidadMostrable(data);
  const pagos = getPagosOrdinariosPositivos(data);
  const pagos6 = getPagosUltimosMeses(data, 6, baseDate);
  const diasSinPago = getDiasSinPago(data, baseDate);

  if (pagos.length === 0) return "sin_historial";
  if (diasSinPago > 180) return "inactivo";

  const pagosGrandes = pagos.filter((p) => mensualidad > 0 && p.cantidad >= mensualidad * 2);
  const pagosCercanosMensualidad = pagos6.filter(
    (p) => mensualidad > 0 && p.cantidad >= mensualidad * 0.85
  );

  const promedio6 =
    pagos6.length > 0 ? pagos6.reduce((acc, p) => acc + p.cantidad, 0) / pagos6.length : 0;

  // Q4: Tendencia — comparar pagos últimos 6m con los 12m anteriores (mes 7-18).
  // Detecta clientes que están MEJORANDO o EMPEORANDO su comportamiento.
  // Solo aplica si hay suficiente historial (≥4 pagos en periodo previo).
  const pagos18 = getPagosUltimosMeses(data, 18, baseDate);
  const pagosPrevios = pagos18.length - pagos6.length; // mes 7-18
  if (pagos.length >= 8 && pagosPrevios >= 4 && mensualidad > 0) {
    const promedioPrev =
      (pagos18.reduce((a, p) => a + p.cantidad, 0) - pagos6.reduce((a, p) => a + p.cantidad, 0)) /
      pagosPrevios;
    const ratio = promedio6 / promedioPrev;
    if (ratio >= 1.25 && pagos6.length >= 3) return "tendencia_positiva";
    if (ratio <= 0.6 && pagos6.length >= 1) return "tendencia_negativa";
  }

  if (pagos6.length >= 5 && promedio6 >= mensualidad * 0.9) {
    return "mensual_constante";
  }

  if (pagos6.length >= 5 && promedio6 < mensualidad * 0.9) {
    return "mensual_constante_bajo";
  }

  if (pagos6.length >= 2 && pagosCercanosMensualidad.length >= 2) {
    return "intermitente_recuperable";
  }

  if (pagosGrandes.length >= 2) {
    return "pagos_grandes_ocasionales";
  }

  if (pagos6.length >= 1) {
    return "irregular_activo";
  }

  return "irregular_riesgoso";
};

/**
 * Clasifica una deuda en categoría de cartera.
 *
 * @param {number} mesesAtraso     — meses-mensualidad de mora financiera
 * @param {number} [saldoAtrasado] — saldo realmente vencido (0 si no se pasa)
 *
 * Categorías:
 *   D            10+ meses de atraso
 *   C            7-9 meses
 *   B            4-6 meses
 *   A            1-3 meses
 *   COLA_FINAL   saldo vencido > 0 pero < 1 mensualidad (cola final de pago)
 *   AL_CORRIENTE sin saldo vencido
 */
/**
 * SA-M5: Cuenta los EPISODIOS DE ATRASO en el historial.
 *
 * Un episodio se define como un período donde el cliente estuvo >30 días sin
 * pagar. Detectamos clientes "primer atraso" (N=1) vs "reincidentes" (N≥3).
 *
 * Algoritmo:
 *   1. Ordena pagos positivos por fecha ASC
 *   2. Para cada gap entre pago[i] y pago[i+1], si > 30 días → cuenta como
 *      episodio
 *   3. Si el último pago tiene > 30d hasta HOY, cuenta episodio actual abierto
 *
 * @returns {object} { episodios, etiqueta, esReincidente }
 */
export const getEpisodiosAtraso = (data = {}, baseDate = new Date()) => {
  const pagos = getPagosPositivos(data);
  if (pagos.length === 0) {
    return { episodios: 0, etiqueta: "sin_historial", esReincidente: false };
  }

  const ordenados = [...pagos].sort(
    (a, b) => a.fechaDate.getTime() - b.fechaDate.getTime(),
  );

  let episodios = 0;
  // Gaps entre pagos consecutivos
  for (let i = 1; i < ordenados.length; i++) {
    const diff = ordenados[i].fechaDate.getTime() - ordenados[i - 1].fechaDate.getTime();
    if (diff > 30 * 86400000) episodios++;
  }

  // Gap actual: último pago hasta hoy
  const ultimo = ordenados[ordenados.length - 1].fechaDate.getTime();
  const gapActual = baseDate.getTime() - ultimo;
  if (gapActual > 30 * 86400000) episodios++;

  let etiqueta = "puntual";
  if (episodios >= 3) etiqueta = "reincidente";
  else if (episodios === 2) etiqueta = "segundo_atraso";
  else if (episodios === 1) etiqueta = "primer_atraso";

  return {
    episodios,
    etiqueta,
    esReincidente: episodios >= 3,
  };
};

/**
 * SA-M3: Score VALOR DEL CLIENTE (LTV).
 *
 * Score 0-100 combinando:
 *   - Antigüedad (años): cap 30 pts
 *   - Total pagado en su vida: cap 40 pts (normalizado por 100k mxn)
 *   - # deudas activas: cap 20 pts (más deudas = más LTV)
 *   - # pagos totales registrados: cap 10 pts
 *
 * Cliente de alto valor amerita mayor esfuerzo de retención y trato preferente.
 *
 * extras: { otrasDeudasActivas, totalPagadoLifetime, pagosTotales }
 *   Si no se pasan, usa solo la deuda actual.
 */
export const calcularValorCliente = (data = {}, baseDate = new Date(), extras = {}) => {
  const desglose = [];
  let score = 0;

  // Antigüedad
  const fechaInicio = getFechaInicioReal(data);
  let bonusAntig = 0;
  if (fechaInicio) {
    const years = (baseDate - fechaInicio) / (365.25 * 86400000);
    bonusAntig = clamp(years * 5, 0, 30);
    desglose.push(`Antigüedad: ${years.toFixed(1)} años × 5 = ${bonusAntig.toFixed(1)} pts (cap 30)`);
  }
  score += bonusAntig;

  // Total pagado lifetime (cliente acumulado, no solo esta deuda)
  const totalPagado = Number(
    extras?.totalPagadoLifetime ?? getTotalPagadoReal(data),
  );
  const bonusPagado = clamp((totalPagado / 100000) * 10, 0, 40);
  score += bonusPagado;
  desglose.push(
    `Total pagado vida: ${agendaCobranza.formatMoney(totalPagado)} / 100k × 10 = ${bonusPagado.toFixed(1)} pts (cap 40)`,
  );

  // # deudas activas del cliente
  const deudasActivas = Number(extras?.otrasDeudasActivas || 0) + 1; // esta + otras
  const bonusDeudas = clamp(deudasActivas * 5, 0, 20);
  score += bonusDeudas;
  desglose.push(`Deudas activas: ${deudasActivas} × 5 = ${bonusDeudas} pts (cap 20)`);

  // # pagos registrados (esta deuda; extras puede pasar el agregado del cliente)
  const numPagos = Number(extras?.pagosTotales ?? (Array.isArray(data?.pagos) ? data.pagos.length : 0));
  const bonusPagos = clamp(numPagos * 0.3, 0, 10);
  score += bonusPagos;
  desglose.push(`Pagos registrados: ${numPagos} × 0.3 = ${bonusPagos.toFixed(1)} pts (cap 10)`);

  score = clamp(score, 0, 100);

  let nivel = "estándar";
  if (score >= 75) nivel = "premium";
  else if (score >= 55) nivel = "alto";
  else if (score >= 30) nivel = "medio";

  desglose.push(`Score final valor cliente: ${score.toFixed(1)} → ${nivel}`);

  return {
    valorCliente: score,
    valorClienteNivel: nivel,
    valorClienteDesglose: desglose,
  };
};

/**
 * SB-M1: Tendencia derivada de snapshots históricos.
 *
 * Compara el saldoAtrasado actual de una deuda contra sus valores en los
 * últimos N snapshots (default 4) y reporta dirección.
 *
 * Diferencia clave vs `detectarPatronPago` que mira PAGOS: este mira cómo
 * está EVOLUCIONANDO el atraso operativo semana a semana, no el patrón
 * histórico de pagos. Útil para detectar cuentas que están deteriorándose
 * aunque sigan teniendo "patrón mensual_constante".
 *
 * @param {string} deudaId
 * @param {Array}  snapshots — colección de snapshots {fechaMs, porDeuda{...}}.
 *                              Ordenada desc o asc; la función lo normaliza.
 * @param {number} saldoActual — saldoAtrasado actual (para incluirlo en serie)
 *
 * @returns { direccion: 'sube'|'baja'|'estable'|'sin_datos',
 *            cambio: number,        // delta absoluto vs el más antiguo
 *            cambioPct: number,     // % de cambio (vs base, 0 si base=0)
 *            puntos: number,        // # snapshots usados (≥2 para considerar)
 *          }
 */
export const calcularTendenciaSnapshot = (
  deudaId,
  snapshots = [],
  saldoActual = 0,
  ventana = 4,
) => {
  if (!deudaId || !Array.isArray(snapshots) || snapshots.length === 0) {
    return { direccion: "sin_datos", cambio: 0, cambioPct: 0, puntos: 0 };
  }
  // Normalizar a ASC por fechaMs
  const ordenados = [...snapshots]
    .filter((s) => s && s.porDeuda)
    .sort((a, b) => Number(a.fechaMs || 0) - Number(b.fechaMs || 0));

  // Últimos `ventana` snapshots
  const ultimos = ordenados.slice(-ventana);

  const serie = ultimos
    .map((s) => Number(s.porDeuda?.[deudaId]?.sa))
    .filter((v) => Number.isFinite(v));

  if (serie.length === 0) {
    return { direccion: "sin_datos", cambio: 0, cambioPct: 0, puntos: 0 };
  }

  // Incluir el valor actual al final de la serie
  serie.push(Number(saldoActual) || 0);

  if (serie.length < 2) {
    return { direccion: "sin_datos", cambio: 0, cambioPct: 0, puntos: serie.length };
  }

  const base = serie[0];
  const ultimo = serie[serie.length - 1];
  const cambio = ultimo - base;
  const cambioPct = base > 0 ? (cambio / base) * 100 : 0;

  // Umbral del 5% para "estable" (evita ruido por centavos / redondeos)
  let direccion = "estable";
  if (Math.abs(cambioPct) >= 5 || Math.abs(cambio) >= 500) {
    direccion = cambio > 0 ? "sube" : "baja";
  }

  return {
    direccion,
    cambio: Math.round(cambio * 100) / 100,
    cambioPct: Math.round(cambioPct * 10) / 10,
    puntos: serie.length,
  };
};

/**
 * SB-U2: Serie temporal de scores (prioridad, recuperabilidad) por deuda.
 * Lee los snapshots que incluyan ps/rs en porDeuda. Devuelve la serie ASC.
 */
export const getHistorialScoresDeuda = (deudaId, snapshots = []) => {
  if (!deudaId || !Array.isArray(snapshots)) return [];
  return [...snapshots]
    .filter((s) => s?.porDeuda?.[deudaId])
    .sort((a, b) => Number(a.fechaMs || 0) - Number(b.fechaMs || 0))
    .map((s) => ({
      fechaISO: s.fechaISO,
      fechaMs: Number(s.fechaMs || 0),
      ps: Number(s.porDeuda[deudaId]?.ps ?? 0),
      rs: Number(s.porDeuda[deudaId]?.rs ?? 0),
      sa: Number(s.porDeuda[deudaId]?.sa ?? 0),
    }));
};

/**
 * IA-C4: Predictor heurístico de probabilidad de pago.
 *
 * Devuelve la probabilidad estimada (0..1) de que esta deuda reciba al menos
 * un pago en los próximos 7 y 30 días. Se basa en:
 *   - Patrón histórico (base prob)
 *   - Días sin pago (decay si lleva mucho)
 *   - Promesa activa próxima (gran boost)
 *   - Promesas incumplidas previas (penalización)
 *   - Episodios reincidentes (penalización)
 *   - Antigüedad + buen patrón (bonus de confianza)
 *   - Tendencia desde snapshots (mejora/empeora)
 *
 * NO es ML real — es calibración con dominio. Reemplazable por modelo entrenado
 * cuando haya suficiente data etiquetada.
 *
 * @param {object} data — doc de deuda
 * @param {Date}   baseDate
 * @param {object} perfil — perfil ya calculado (patrón, días sin pago, etc.)
 * @param {object} extras — { promesaProxima: {fechaISO, dias}, promesasIncumplidas, snapshots }
 */
export const calcularProbabilidadPago = (
  data = {},
  baseDate = new Date(),
  perfil = {},
  extras = {},
) => {
  const factores = [];

  // ── Base por patrón histórico ────────────────────────────────────────
  const patron = perfil.patronPago || detectarPatronPago(data, baseDate);
  const BASE_POR_PATRON = {
    mensual_constante: 0.82,
    mensual_constante_bajo: 0.7,
    tendencia_positiva: 0.72,
    intermitente_recuperable: 0.5,
    pagos_grandes_ocasionales: 0.4,
    irregular_activo: 0.35,
    tendencia_negativa: 0.25,
    irregular_riesgoso: 0.18,
    inactivo: 0.07,
    sin_historial: 0.3,
  };
  let prob30d = BASE_POR_PATRON[patron] ?? 0.3;
  factores.push(`Patrón ${patron}: base ${(prob30d * 100).toFixed(0)}%`);

  // ── Decay por días sin pago ──────────────────────────────────────────
  const diasSinPago = Number(perfil.diasSinPago ?? getDiasSinPago(data, baseDate));
  let multDias = 1;
  if (diasSinPago > 365) multDias = 0.15;
  else if (diasSinPago > 180) multDias = 0.4;
  else if (diasSinPago > 90) multDias = 0.7;
  else if (diasSinPago > 60) multDias = 0.85;
  if (multDias !== 1) {
    prob30d *= multDias;
    factores.push(`Días sin pago ${diasSinPago} → ×${multDias.toFixed(2)}`);
  }

  // ── Episodios reincidentes ───────────────────────────────────────────
  const epis = Number(perfil.episodios || 0);
  if (perfil.esReincidente || epis >= 3) {
    prob30d *= 0.75;
    factores.push(`Reincidente (${epis} episodios) → ×0.75`);
  }

  // ── Promesas incumplidas previas ─────────────────────────────────────
  const promesasIncumplidas = Number(extras?.promesasIncumplidas || 0);
  if (promesasIncumplidas >= 2) {
    prob30d *= 0.55;
    factores.push(`${promesasIncumplidas} promesas incumplidas → ×0.55`);
  } else if (promesasIncumplidas === 1) {
    prob30d *= 0.75;
    factores.push("1 promesa incumplida → ×0.75");
  }

  // ── Antigüedad + buen patrón (bonus de confianza) ────────────────────
  const fechaInicio = getFechaInicioReal(data);
  if (fechaInicio) {
    const years = (baseDate - fechaInicio) / (365.25 * 86400000);
    if (
      years >= 5 &&
      ["mensual_constante", "mensual_constante_bajo", "tendencia_positiva"].includes(patron)
    ) {
      prob30d *= 1.1;
      factores.push(`Antigüedad ${years.toFixed(1)}a + patrón confiable → ×1.10`);
    }
  }

  // ── Tendencia snapshot (sube/baja saldo atrasado) ────────────────────
  const tend = perfil.tendenciaSnapshot;
  if (tend?.direccion === "sube") {
    prob30d *= 0.85;
    factores.push("Tendencia atraso ↑ → ×0.85");
  } else if (tend?.direccion === "baja") {
    prob30d *= 1.1;
    factores.push("Tendencia atraso ↓ → ×1.10");
  }

  // ── Promesa próxima activa ───────────────────────────────────────────
  // extras.promesaProxima = { dias: número de días desde hoy hasta fecha promesa }
  let prob7d;
  const prox = extras?.promesaProxima;
  if (prox && typeof prox.dias === "number") {
    if (prox.dias <= 0) {
      // Vence hoy o ya venció pero está pendiente
      prob30d = Math.max(prob30d, 0.7);
      prob7d = Math.max(prob30d, 0.7);
      factores.push("Promesa vence HOY → piso 70%");
    } else if (prox.dias <= 7) {
      prob30d = Math.max(prob30d, 0.6);
      prob7d = Math.max(prob30d * 0.85, 0.5);
      factores.push(`Promesa en ${prox.dias}d → boost`);
    } else if (prox.dias <= 30) {
      prob30d = Math.max(prob30d, 0.5);
      factores.push(`Promesa en ${prox.dias}d → boost moderado`);
    }
  }

  // Si no se calculó prob7d por promesa, derivar de prob30d.
  // Empíricamente ~30-35% de los pagos del mes ocurren en primera semana.
  if (prob7d === undefined) {
    prob7d = prob30d * 0.32;
  }

  prob30d = Math.min(0.97, Math.max(0.01, prob30d));
  prob7d = Math.min(prob30d, Math.max(0.005, prob7d));

  // Etiqueta cualitativa
  let etiquetaProb = "baja";
  if (prob30d >= 0.7) etiquetaProb = "alta";
  else if (prob30d >= 0.4) etiquetaProb = "media";

  return {
    prob7d: Math.round(prob7d * 1000) / 1000,
    prob30d: Math.round(prob30d * 1000) / 1000,
    probabilidadEtiqueta: etiquetaProb,
    probabilidadFactores: factores,
  };
};

export const getCarteraCategoriaV2 = (mesesAtraso = 0, saldoAtrasado = 0) => {
  if (mesesAtraso >= 10) return "D";
  if (mesesAtraso >= 7) return "C";
  if (mesesAtraso >= 4) return "B";
  if (mesesAtraso >= 1) return "A";
  if (Number(saldoAtrasado) > 0) return "COLA_FINAL";
  return "AL_CORRIENTE";
};

export const getCarteraDescripcionV2 = (cartera = "") => {
  switch (cartera) {
    case "A":
      return "1 a 3 meses";
    case "B":
      return "4 a 6 meses";
    case "C":
      return "7 a 9 meses";
    case "D":
      return "10+ meses";
    case "COLA_FINAL":
      return "Cola final (saldo < 1 mensualidad)";
    default:
      return "Sin atraso";
  }
};

export const calcularRecuperabilidad = (data = {}, baseDate = new Date(), extras = {}) => {
  const patron = detectarPatronPago(data, baseDate);
  const diasSinPago = getDiasSinPago(data, baseDate);
  const mesesAtraso = getMesesAtrasoFinanciero(data, baseDate);
  const saldoRestante = agendaCobranza.calcularSaldoMostrable(data);
  const saldoAtrasado = getSaldoAtrasadoReal(data, baseDate);
  const pagos6 = getPagosUltimosMeses(data, 6, baseDate);

  const desglose = [];
  let score = 0;

  let puntosDias = 0;
  if (diasSinPago <= 30) puntosDias = 30;
  else if (diasSinPago <= 60) puntosDias = 22;
  else if (diasSinPago <= 90) puntosDias = 14;
  else if (diasSinPago <= 180) puntosDias = 6;
  else puntosDias = -15;

  score += puntosDias;
  desglose.push(`Días sin pago: ${diasSinPago} → ${puntosDias} pts`);

  const puntosFrecuencia = clamp(pagos6.length * 5, 0, 30);
  score += puntosFrecuencia;
  desglose.push(`Pagos últimos 6 meses: ${pagos6.length} × 5 = ${puntosFrecuencia} pts`);

  // Q4: tendencia detectada en patrón
  let puntosPatron = 0;
  if (patron === "mensual_constante") puntosPatron = 25;
  if (patron === "mensual_constante_bajo") puntosPatron = 20;
  if (patron === "tendencia_positiva") puntosPatron = 22; // cliente mejorando
  if (patron === "tendencia_negativa") puntosPatron = -10; // cliente empeorando
  if (patron === "intermitente_recuperable") puntosPatron = 15;
  if (patron === "pagos_grandes_ocasionales") puntosPatron = 12;
  if (patron === "inactivo") puntosPatron = -25;

  score += puntosPatron;
  desglose.push(`Patrón de pago: ${patron} → ${puntosPatron} pts`);

  let puntosSaldoBajo = 0;
  if (saldoRestante > 0 && saldoAtrasado / saldoRestante <= 0.25) {
    puntosSaldoBajo = 10;
  }

  score += puntosSaldoBajo;
  desglose.push(
    `Atraso vs saldo restante: ${
      saldoRestante > 0 ? ((saldoAtrasado / saldoRestante) * 100).toFixed(1) : 0
    }% → ${puntosSaldoBajo} pts`
  );

  let penalizacionMeses = 0;
  if (mesesAtraso >= 10) penalizacionMeses = -12;

  score += penalizacionMeses;
  desglose.push(`Meses de atraso: ${mesesAtraso} → ${penalizacionMeses} pts`);

  // Q2: Penalización por promesa incumplida.
  // El cliente que prometió pagar y no cumplió tiene menor probabilidad de
  // honrar el próximo compromiso → reducir recuperabilidad significativamente.
  const promesasIncumplidas = Number(extras?.promesasIncumplidas || 0);
  let penalizacionPromesa = 0;
  if (promesasIncumplidas >= 2) penalizacionPromesa = -25;
  else if (promesasIncumplidas === 1) penalizacionPromesa = -15;
  if (penalizacionPromesa !== 0) {
    score += penalizacionPromesa;
    desglose.push(`Promesas incumplidas: ${promesasIncumplidas} → ${penalizacionPromesa} pts`);
  }

  // Q3: Bonus por antigüedad. Clientes con relación larga + buen patrón
  // merecen más esfuerzo de recuperación: tienen capital de confianza.
  const fechaInicio = getFechaInicioReal(data);
  if (fechaInicio) {
    const antigYears = (baseDate - fechaInicio) / (365.25 * 86400000);
    let bonusAntig = 0;
    if (antigYears >= 5 && ["mensual_constante", "mensual_constante_bajo", "tendencia_positiva", "intermitente_recuperable"].includes(patron)) {
      bonusAntig = 10;
    } else if (antigYears >= 3 && ["mensual_constante", "tendencia_positiva"].includes(patron)) {
      bonusAntig = 6;
    }
    if (bonusAntig > 0) {
      score += bonusAntig;
      desglose.push(`Antigüedad ${antigYears.toFixed(1)} años + patrón confiable → +${bonusAntig} pts`);
    }
  }

  // SA-M4: Multiplicador por fraccionamiento. Configurable en
  // `fraccionamientos/{id}.calibracionCobranza.multiplicadorRecuperabilidad`.
  // Default 1.0 = sin ajuste. Permite zonas más "duras" (0.8) o más "fáciles" (1.1).
  const multRecup = Number(extras?.calibracion?.multiplicadorRecuperabilidad || 1);
  if (multRecup !== 1) {
    const previo = score;
    score = score * multRecup;
    desglose.push(`Calibración fraccionamiento: ${previo.toFixed(1)} × ${multRecup} = ${score.toFixed(1)} pts`);
  }

  score = clamp(score, 0, 100);

  let recuperabilidad = "baja";
  if (score >= 70) recuperabilidad = "alta";
  else if (score >= 40) recuperabilidad = "media";

  desglose.push(`Score final recuperabilidad: ${score} → ${recuperabilidad}`);

  return {
    recuperabilidad,
    recuperabilidadScore: score,
    recuperabilidadDesglose: desglose,
  };
};

export const calcularPrioridad = (data = {}, baseDate = new Date(), extras = {}) => {
  const saldoAtrasado = getSaldoAtrasadoReal(data, baseDate);
  const saldoRestante = agendaCobranza.calcularSaldoMostrable(data);
  const mesesAtraso = getMesesAtrasoFinanciero(data, baseDate);
  const diasSinPago = getDiasSinPago(data, baseDate);
  const mensualidad = agendaCobranza.getMensualidadMostrable(data);
  const { recuperabilidadScore } = calcularRecuperabilidad(data, baseDate, extras);

  const porcentajeAtrasado = saldoRestante > 0 ? saldoAtrasado / saldoRestante : 0;

  const puntosMeses = mesesAtraso * 8;
  const puntosPorcentaje = clamp(porcentajeAtrasado * 35, 0, 35);

  // Q1: Saldo normalizado por mensualidad — antes era /2500 (constante mágica
  // que castigaba clientes con mensualidad baja). Ahora "cuántas mensualidades
  // de atraso en monto absoluto" × 3. Escala correctamente para cualquier rango.
  // 10 mensualidades atrasadas = +30 pts (igual que antes para deudas con mens
  // ~$2,500). Pero para mens $500, 10 atrasadas = $5,000 → ahora 30 pts (antes 2).
  const mensualidadesAtrasadasMonto =
    mensualidad > 0 ? saldoAtrasado / mensualidad : 0;
  const puntosSaldo = clamp(mensualidadesAtrasadasMonto, 0, 10) * 3;
  const puntosDias = clamp(diasSinPago / 10, 0, 20);

  let ajusteRecuperabilidad = 0;
  if (recuperabilidadScore >= 70) ajusteRecuperabilidad = -10;
  if (recuperabilidadScore <= 25) ajusteRecuperabilidad = 10;

  let score =
    puntosMeses +
    puntosPorcentaje +
    puntosSaldo +
    puntosDias +
    ajusteRecuperabilidad;

  // SA-M4: Multiplicador por fraccionamiento. Zonas más duras (>1) aumentan
  // prioridad; zonas con clientes muy buenos (<1) reducen.
  const multPrio = Number(extras?.calibracion?.multiplicadorPrioridad || 1);
  if (multPrio !== 1) score = score * multPrio;

  score = clamp(score, 0, 100);

  let prioridad = "baja";
  if (score >= 75) prioridad = "critica";
  else if (score >= 55) prioridad = "alta";
  else if (score >= 30) prioridad = "media";

  const prioridadDesglose = [
    `Meses atraso: ${mesesAtraso} × 8 = ${puntosMeses.toFixed(1)} pts`,
    `% atraso: ${(porcentajeAtrasado * 100).toFixed(1)}% × 35 = ${puntosPorcentaje.toFixed(1)} pts`,
    `Saldo atrasado: ${mensualidadesAtrasadasMonto.toFixed(1)} mensualidades × 3 = ${puntosSaldo.toFixed(1)} pts`,
    `Días sin pago: ${diasSinPago} / 10 = ${puntosDias.toFixed(1)} pts`,
    `Ajuste por recuperabilidad (${recuperabilidadScore}): ${ajusteRecuperabilidad} pts`,
    multPrio !== 1 ? `Calibración fraccionamiento: × ${multPrio}` : null,
    `Score final prioridad: ${score.toFixed(1)} → ${prioridad}`,
  ].filter(Boolean);

  return {
    prioridad,
    prioridadScore: score,
    prioridadDesglose,
  };
};

export const calcularPagosSugeridos = (data = {}, baseDate = new Date()) => {
  const mensualidad = agendaCobranza.getMensualidadMostrable(data);
  const saldoAtrasado = getSaldoAtrasadoReal(data, baseDate);
  const saldoRestante = agendaCobranza.calcularSaldoMostrable(data);
  const mesesAtraso = getMesesAtrasoFinanciero(data, baseDate);
  const plazoMesesPg = getPlazoMeses(data);
  const mesesRealesPg = getMesesTranscurridosReales(data, baseDate);
  const fueraPlazoPg = plazoMesesPg > 0 && mesesRealesPg > plazoMesesPg;

  // PDF-Bug4 + 6:
  // - paraCorriente: liquida el atraso. Si fuera de plazo, usa saldoRestante
  //   (para no dejar centavos por diferencia de redondeo entre atraso y saldo).
  // - pagoMinimo: opción de contención, ~ 30% del objetivo o 1 mensualidad.
  // - pagoIdeal: paraCorriente + buffer pequeño para asegurar liquidación.
  const objetivo = fueraPlazoPg
    ? Math.max(saldoAtrasado, saldoRestante)
    : Math.min(saldoAtrasado, saldoRestante);

  const pagoParaCorriente = Math.round(objetivo * 100) / 100;

  const pagoMinimo = Math.min(
    Math.max(mensualidad, objetivo * 0.3),
    pagoParaCorriente,
  );

  // Ideal: redondea al alza para asegurar liquidación neta (sin remanentes).
  // Si fueraPlazo, pagoIdeal === pagoParaCorriente exacto (ya liquida todo).
  const pagoIdeal = fueraPlazoPg
    ? pagoParaCorriente
    : Math.min(pagoParaCorriente + mensualidad * 0.1, saldoRestante);

  let pagoParaBajarCartera = 0;
  let objetivoCartera = "Mantener seguimiento";

  if (mesesAtraso >= 10) {
    pagoParaBajarCartera = Math.min((mesesAtraso - 9) * mensualidad, saldoAtrasado);
    objetivoCartera = "Bajar de cartera D a C";
  } else if (mesesAtraso >= 7) {
    pagoParaBajarCartera = Math.min((mesesAtraso - 6) * mensualidad, saldoAtrasado);
    objetivoCartera = "Bajar de cartera C a B";
  } else if (mesesAtraso >= 4) {
    pagoParaBajarCartera = Math.min((mesesAtraso - 3) * mensualidad, saldoAtrasado);
    objetivoCartera = "Bajar de cartera B a A";
  } else if (mesesAtraso >= 1) {
    pagoParaBajarCartera = saldoAtrasado;
    objetivoCartera = "Poner al corriente";
  }

  return {
    pagoMinimo,
    pagoIdeal,
    pagoParaCorriente,
    pagoParaBajarCartera,
    objetivoCartera,
  };
};

export const generarAlertas = (data = {}, perfilBase = {}) => {
  const alertas = [];
  const patron = perfilBase.patronPago;
  const saldoRestante = perfilBase.saldoRestante || 0;
  const saldoAtrasado = perfilBase.saldoAtrasado || 0;
  const mensualidad = perfilBase.mensualidad || 0;
  const diasSinPago = perfilBase.diasSinPago || 0;
  const porcentajeLiquidacion = perfilBase.porcentajeLiquidacion || 0;

  if (patron === "mensual_constante_bajo") {
    alertas.push("Atraso silencioso: paga constante, pero por debajo de lo pactado");
  }

  if (diasSinPago >= 120) {
    alertas.push("Mucho tiempo sin registrar pago");
  }

  if (porcentajeLiquidacion >= 85) {
    alertas.push("Cliente cerca de liquidar");
  }

  if (saldoRestante > 0 && saldoAtrasado / saldoRestante >= 0.6) {
    alertas.push("El atraso representa gran parte del saldo restante");
  }

  if (mensualidad > 0 && saldoRestante <= mensualidad * 3) {
    alertas.push("Posible cierre con pocas mensualidades");
  }

  return alertas;
};

export const generarAccionRecomendada = (perfil = {}) => {
    if (perfil.oportunidad === "cierre_rapido") {
  return `Ofrecer cierre/liquidación por ${agendaCobranza.formatMoney(
    perfil.saldoRestante
  )}.`;
}
  const {
    prioridad,
    recuperabilidad,
    patronPago,
    porcentajeLiquidacion,
    mesesAtraso,
    planTitulo,
  } = perfil;

  if (planTitulo) {
    return `${planTitulo}. ${perfil.textoPlan || ""}`;
  }

  if (porcentajeLiquidacion >= 85) {
    return "Ofrecer cierre/liquidación para terminar la cuenta.";
  }

  if (patronPago === "mensual_constante_bajo") {
    return "Llamar y explicar atraso silencioso; pedir ajuste de pago mensual o regularización parcial.";
  }

  if (prioridad === "critica" && recuperabilidad === "alta") {
    return "Llamar hoy y ofrecer regularización parcial con monto objetivo.";
  }

  if (prioridad === "critica" && recuperabilidad === "baja") {
    return "Escalar a revisión directiva; preparar estrategia formal de recuperación.";
  }

  if (mesesAtraso >= 4 && recuperabilidad !== "baja") {
    return "Negociar convenio corto para bajar cartera.";
  }

  if (prioridad === "media") {
    return "Enviar recordatorio firme y programar seguimiento.";
  }

  return "Mantener seguimiento preventivo.";
};

export const calcularPlanRegularizacion = (data = {}, baseDate = new Date()) => {
  const mensualidad = agendaCobranza.getMensualidadMostrable(data);
  const saldoAtrasado = getSaldoAtrasadoReal(data, baseDate);
  const saldoRestante = agendaCobranza.calcularSaldoMostrable(data);
  const mesesAtraso = getMesesAtrasoFinanciero(data, baseDate);
  const diasSinPago = getDiasSinPago(data, baseDate);
  const patronPago = detectarPatronPago(data, baseDate);

  if (mensualidad <= 0 || saldoAtrasado <= 0 || saldoRestante <= 0) {
    return {
      planTipo: "sin_plan",
      planTitulo: "Sin atraso recuperable",
      pagoInicial: 0,
      mensualidadActual: mensualidad,
      mensualidadSugerida3Meses: mensualidad,
      mensualidadSugerida6Meses: mensualidad,
      pagoUnicoRegularizacion: 0,
      textoPlan: "No requiere plan de regularización.",
    };
  }

  // PDF-Bug6: si está fuera de plazo, el objetivo es liquidar (saldoRestante)
  // no solo cubrir atraso teórico. Si saldoAtrasado ≠ saldoRestante por redondeos,
  // usar el mayor para no dejar centavos colgando.
  const plazoMesesPlan = getPlazoMeses(data);
  const mesesReales = getMesesTranscurridosReales(data, baseDate);
  const fueraPlazoPlan = plazoMesesPlan > 0 && mesesReales > plazoMesesPlan;
  const objetivoLiquidacion = fueraPlazoPlan
    ? Math.max(saldoAtrasado, saldoRestante)
    : Math.min(saldoAtrasado, saldoRestante);

  const pagoUnicoRegularizacion = objetivoLiquidacion;

  // PDF-Bug4: diferenciar mínimo / ideal / corriente
  // - mínimo: 1 mensualidad o 30% del objetivo (lo que sea menor), pero >= 1 mens
  // - ideal: objetivo + 10% buffer (incentiva pagar de más para adelantar)
  // - para corriente: objetivo total
  const pagoInicial = Math.min(
    Math.max(mensualidad, objetivoLiquidacion * 0.3),
    objetivoLiquidacion,
  );

  // PDF-Bug3: planes 3M / 6M ahora se calculan sobre el OBJETIVO real, no sobre
  // "atraso + mensualidad fantasma futura". Si la cuenta está fuera de plazo,
  // dividir el saldoRestante en N pagos parejos.
  const mensualidadSugerida3Meses = fueraPlazoPlan
    ? Math.ceil((objetivoLiquidacion / 3) * 100) / 100
    : Math.min(mensualidad + Math.max(objetivoLiquidacion - pagoInicial, 0) / 3, saldoRestante);

  const mensualidadSugerida6Meses = fueraPlazoPlan
    ? Math.ceil((objetivoLiquidacion / 6) * 100) / 100
    : Math.min(mensualidad + Math.max(objetivoLiquidacion - pagoInicial, 0) / 6, saldoRestante);

  let planTipo = "regularizacion";
  let planTitulo = "Regularización parcial";

  if (saldoRestante <= mensualidad * 3) {
    planTipo = "cierre";
    planTitulo = "Proponer cierre/liquidación";
  } else if (patronPago === "mensual_constante_bajo") {
    planTipo = "ajuste_mensual";
    planTitulo = "Ajustar mensualidad por atraso silencioso";
  } else if (mesesAtraso >= 7 && diasSinPago <= 90) {
    planTipo = "rescate";
    planTitulo = "Rescate de cuenta recuperable";
  } else if (mesesAtraso >= 7 && diasSinPago > 120) {
    planTipo = "convenio_formal";
    planTitulo = "Convenio formal de recuperación";
  }

  const textoPlan =
    planTipo === "cierre"
      ? `Ofrecer liquidación/cierre por ${agendaCobranza.formatMoney(pagoUnicoRegularizacion)}.`
      : planTipo === "ajuste_mensual"
      ? `Solicitar pago inicial de ${agendaCobranza.formatMoney(
          pagoInicial
        )} y ajustar mensualidad a ${agendaCobranza.formatMoney(
          mensualidadSugerida6Meses
        )} durante 6 meses.`
      : `Proponer pago inicial de ${agendaCobranza.formatMoney(
          pagoInicial
        )}. Para regularizar en 3 meses: ${agendaCobranza.formatMoney(
          mensualidadSugerida3Meses
        )}/mes. En 6 meses: ${agendaCobranza.formatMoney(
          mensualidadSugerida6Meses
        )}/mes.`;

  return {
    planTipo,
    planTitulo,
    pagoInicial,
    mensualidadActual: mensualidad,
    mensualidadSugerida3Meses,
    mensualidadSugerida6Meses,
    pagoUnicoRegularizacion,
    textoPlan,
  };
};

export const calcularOportunidadCobranza = (perfil = {}) => {
  const {
    saldoRestante = 0,
    saldoAtrasado = 0,
    mensualidad = 0,
    porcentajeLiquidacion = 0,
    diasSinPago = 0,
    recuperabilidad = "baja",
    planTipo = "",
    fueraDePlazo = false,
    candidatoLiquidacion = false,
  } = perfil;

  // Q5: Distinguir cierre rápido normal (cuenta cerrando dentro de plazo,
  // operación estándar) vs urgente (fuera de plazo, debe liquidarse YA).
  const cierreUrgente = candidatoLiquidacion || (fueraDePlazo && saldoRestante > 0);
  const cierreNormal =
    saldoRestante > 0 &&
    mensualidad > 0 &&
    (saldoRestante <= mensualidad * 3 || porcentajeLiquidacion >= 0.9);

  if (planTipo === "cierre" || cierreUrgente) {
    return {
      oportunidad: cierreUrgente ? "cierre_urgente" : "cierre_rapido",
      oportunidadLabel: cierreUrgente ? "Cierre URGENTE (fuera de plazo)" : "Cierre rápido",
      oportunidadScore: cierreUrgente ? 98 : 95,
    };
  }

  if (cierreNormal) {
    return {
      oportunidad: "cierre_rapido",
      oportunidadLabel: "Cierre rápido",
      oportunidadScore: 90,
    };
  }

  if (recuperabilidad === "alta" && saldoAtrasado > 0) {
    return {
      oportunidad: "recuperacion_alta",
      oportunidadLabel: "Recuperación alta",
      oportunidadScore: 80,
    };
  }

  if (recuperabilidad === "media" && saldoAtrasado > 0) {
    return {
      oportunidad: "convenio",
      oportunidadLabel: "Convenio",
      oportunidadScore: 60,
    };
  }

  if (diasSinPago >= 180) {
    return {
      oportunidad: "riesgo_legal",
      oportunidadLabel: "Riesgo legal",
      oportunidadScore: 40,
    };
  }

  return {
    oportunidad: "seguimiento",
    oportunidadLabel: "Seguimiento",
    oportunidadScore: 50,
  };
};

export const calcularPerfilCobranzaV2 = (data = {}, baseDate = new Date(), extras = {}) => {
    
    if (!isDeudaCobrable(data)) {
        
  return {
    esCobrable: false,
  };
}
  const saldoRestante = agendaCobranza.calcularSaldoMostrable(data);
  const saldoAtrasado = getSaldoAtrasadoReal(data, baseDate);
  const mensualidad = agendaCobranza.getMensualidadMostrable(data);
  const totalPagado = getTotalPagadoReal(data);
  const totalPagadoOrdinario = getTotalPagadoOrdinarioReal(data);
  const montoTotal = agendaCobranza.toNumber(data?.monto ?? data?.importe);
  const enganche = agendaCobranza.toNumber(data?.enganche);

  const mesesAtraso = getMesesAtrasoFinanciero(data, baseDate);
  const mesesTranscurridos = getMesesVencidosParaAtraso(data, baseDate);
  // PDF-Bug1: meses transcurridos REALES (sin tope al plazo). Útil para mostrar
  // "ya pasaron 53 meses" en lugar de "36" cuando la deuda venció hace mucho.
  const mesesTranscurridosReales = getMesesTranscurridosReales(data, baseDate);
  const plazoMeses = getPlazoMeses(data);
  const fueraDePlazo = plazoMeses > 0 && mesesTranscurridosReales > plazoMeses;
  // PDF-Bug7: cuenta cuasi-liquidada (saldo < 2.5 mensualidades) Y fuera de plazo
  // es candidato natural a liquidación final.
  const candidatoLiquidacion =
    fueraDePlazo && mensualidad > 0 && saldoRestante > 0 && saldoRestante <= mensualidad * 2.5;
  const diasSinPago = getDiasSinPago(data, baseDate);
  const patronPago = detectarPatronPago(data, baseDate);
  // PDF-Bug7: si fueraDePlazo + cuasi-liquidado, fuerza COLA_FINAL para reflejar
  // el verdadero estado operativo (no es "cartera B" ni "A", está cerrándose).
  let cartera = getCarteraCategoriaV2(mesesAtraso, saldoAtrasado);
  if (candidatoLiquidacion && cartera !== "D" && cartera !== "C") {
    cartera = "COLA_FINAL";
  }
  const carteraDescripcion = getCarteraDescripcionV2(cartera);

  const recuperabilidadData = calcularRecuperabilidad(data, baseDate, extras);
  const prioridadData = calcularPrioridad(data, baseDate, extras);
  const episodiosData = getEpisodiosAtraso(data, baseDate);
  const valorClienteData = calcularValorCliente(data, baseDate, extras);
  const pagos = calcularPagosSugeridos(data, baseDate);
  const planRegularizacion = calcularPlanRegularizacion(data, baseDate);

  // SB-M1: tendencia derivada de snapshots históricos.
  // El panel pasa extras.snapshots y extras.deudaId.
  const tendenciaSnapshot = calcularTendenciaSnapshot(
    extras?.deudaId,
    extras?.snapshots,
    saldoAtrasado,
  );

  // IA-C4: probabilidad de pago (necesita perfil parcial; armamos uno mini)
  const perfilParaProb = {
    patronPago,
    diasSinPago,
    episodios: episodiosData.episodios,
    esReincidente: episodiosData.esReincidente,
    tendenciaSnapshot,
  };
  const probabilidadPago = calcularProbabilidadPago(
    data,
    baseDate,
    perfilParaProb,
    extras,
  );

  // Valor esperado 30d: cuánto razonablemente se recuperará si se gestiona.
  // Usa pagoParaCorriente como techo (lo que se le pediría) capeado a saldoRestante.
  const techoPago = Math.min(
    Math.max(pagos.pagoParaCorriente || 0, mensualidad || 0),
    saldoRestante,
  );
  const valorEsperado30d = Math.round(techoPago * probabilidadPago.prob30d * 100) / 100;
  const valorEsperado7d = Math.round(techoPago * probabilidadPago.prob7d * 100) / 100;

  const porcentajeAtrasado = saldoRestante > 0 ? saldoAtrasado / saldoRestante : 0;
  const porcentajeLiquidacion = montoTotal > 0 ? totalPagado / montoTotal : 0;

const perfilBase = {
  saldoRestante,
  saldoAtrasado,
  mensualidad,
  totalPagado,
  totalPagadoOrdinario,
  enganche,
  montoTotal,
  mesesAtraso,
  mesesTranscurridos,
  mesesTranscurridosReales,
  plazoMeses,
  fueraDePlazo,
  candidatoLiquidacion,
  diasSinPago,
  patronPago,
  cartera,
  carteraDescripcion,
  porcentajeAtrasado,
  porcentajeLiquidacion,
  ...recuperabilidadData,
  ...prioridadData,
  ...pagos,
  ...planRegularizacion,
  ...episodiosData,
  ...valorClienteData,
  tendenciaSnapshot,
  ...probabilidadPago,
  valorEsperado30d,
  valorEsperado7d,
};

const oportunidadData = calcularOportunidadCobranza(perfilBase);

const perfilConOportunidad = {
  ...perfilBase,
  ...oportunidadData,
};

const alertas = generarAlertas(data, perfilConOportunidad);
const accion = generarAccionRecomendada(perfilConOportunidad);

const rankingScore =
  perfilConOportunidad.prioridadScore * 0.45 +
  perfilConOportunidad.recuperabilidadScore * 0.25 +
  perfilConOportunidad.oportunidadScore * 0.2 +
  clamp(saldoAtrasado / 3000, 0, 10);
  const explicaciones = {
  score: [
    `Ranking operativo: ${Math.round(rankingScore)}`,
    `Prioridad: ${perfilConOportunidad.prioridadScore.toFixed(1)} × 0.45 = ${(perfilConOportunidad.prioridadScore * 0.45).toFixed(1)}`,
    `Recuperabilidad: ${perfilConOportunidad.recuperabilidadScore.toFixed(1)} × 0.25 = ${(perfilConOportunidad.recuperabilidadScore * 0.25).toFixed(1)}`,
    `Oportunidad: ${perfilConOportunidad.oportunidadScore.toFixed(1)} × 0.20 = ${(perfilConOportunidad.oportunidadScore * 0.2).toFixed(1)}`,
    `Monto atrasado normalizado: ${clamp(saldoAtrasado / 3000, 0, 10).toFixed(1)} pts`,
  ].join("\n"),

  saldoAtrasado: [
    `Monto total: ${agendaCobranza.formatMoney(montoTotal)}`,
    `Enganche: ${agendaCobranza.formatMoney(enganche)}`,
    `Mensualidad: ${agendaCobranza.formatMoney(mensualidad)}`,
    `Meses transcurridos: ${mesesTranscurridos}`,
    `Esperado: ${mesesTranscurridos} × ${agendaCobranza.formatMoney(mensualidad)}`,
    `Pagos ordinarios: ${agendaCobranza.formatMoney(totalPagadoOrdinario)}`,
    `Total pagado para saldo: ${agendaCobranza.formatMoney(totalPagado)}`,
    `Saldo restante: ${agendaCobranza.formatMoney(saldoRestante)}`,
    `Saldo atrasado final: ${agendaCobranza.formatMoney(saldoAtrasado)}`,
    `Nota: el atraso se limita al saldo restante.`,
  ].join("\n"),

  mesesAtraso: [
    `Saldo atrasado: ${agendaCobranza.formatMoney(saldoAtrasado)}`,
    `Mensualidad: ${agendaCobranza.formatMoney(mensualidad)}`,
    `Cálculo: floor(${saldoAtrasado.toFixed(2)} / ${mensualidad.toFixed(2)})`,
    `Resultado: ${mesesAtraso} meses financieros de atraso`,
    `Cartera: ${cartera} (${carteraDescripcion})`,
  ].join("\n"),

  prioridad: perfilConOportunidad.prioridadDesglose?.join("\n") || "Sin desglose.",

  recuperabilidad:
    perfilConOportunidad.recuperabilidadDesglose?.join("\n") || "Sin desglose.",

  oportunidad: [
    `Oportunidad: ${perfilConOportunidad.oportunidadLabel}`,
    `Plan tipo: ${perfilConOportunidad.planTipo}`,
    `Saldo restante: ${agendaCobranza.formatMoney(saldoRestante)}`,
    `Mensualidad: ${agendaCobranza.formatMoney(mensualidad)}`,
    `Porcentaje liquidado: ${(porcentajeLiquidacion * 100).toFixed(1)}%`,
    `Días sin pago: ${diasSinPago}`,
    `Recuperabilidad: ${perfilConOportunidad.recuperabilidad}`,
    `Score oportunidad: ${perfilConOportunidad.oportunidadScore}`,
  ].join("\n"),

  plan: [
    `Plan: ${perfilConOportunidad.planTitulo}`,
    `Tipo: ${perfilConOportunidad.planTipo}`,
    `Pago inicial: ${agendaCobranza.formatMoney(perfilConOportunidad.pagoInicial)}`,
    `Pago para corriente: ${agendaCobranza.formatMoney(perfilConOportunidad.pagoParaCorriente)}`,
    `Mensualidad actual: ${agendaCobranza.formatMoney(perfilConOportunidad.mensualidadActual)}`,
    `Mensualidad sugerida 3M: ${agendaCobranza.formatMoney(perfilConOportunidad.mensualidadSugerida3Meses)}`,
    `Mensualidad sugerida 6M: ${agendaCobranza.formatMoney(perfilConOportunidad.mensualidadSugerida6Meses)}`,
    `Texto plan: ${perfilConOportunidad.textoPlan}`,
  ].join("\n"),

  pagoMinimo: [
    `Pago mínimo sugerido: ${agendaCobranza.formatMoney(perfilConOportunidad.pagoMinimo)}`,
    `Mensualidad: ${agendaCobranza.formatMoney(mensualidad)}`,
    `Saldo atrasado: ${agendaCobranza.formatMoney(saldoAtrasado)}`,
    `Saldo restante: ${agendaCobranza.formatMoney(saldoRestante)}`,
    `Regla: mínimo entre 2 mensualidades, saldo atrasado y saldo restante.`,
  ].join("\n"),

  pagoIdeal: [
    `Pago ideal sugerido: ${agendaCobranza.formatMoney(perfilConOportunidad.pagoIdeal)}`,
    `Mensualidad: ${agendaCobranza.formatMoney(mensualidad)}`,
    `Saldo atrasado: ${agendaCobranza.formatMoney(saldoAtrasado)}`,
    `Saldo restante: ${agendaCobranza.formatMoney(saldoRestante)}`,
    `Regla: mínimo entre 4 mensualidades, saldo atrasado y saldo restante.`,
  ].join("\n"),

  accion: [
    `Acción recomendada: ${accion}`,
    `Prioridad: ${perfilConOportunidad.prioridad}`,
    `Recuperabilidad: ${perfilConOportunidad.recuperabilidad}`,
    `Oportunidad: ${perfilConOportunidad.oportunidadLabel}`,
    `Plan: ${perfilConOportunidad.planTitulo}`,
    `Alerta principal: ${alertas[0] || "Sin alerta crítica"}`,
  ].join("\n"),
};

return {
    esCobrable: true,
  ...perfilConOportunidad,
  alertas,
  alertaPrincipal: alertas[0] || "Sin alerta crítica",
  accion,
  explicaciones,
  rankingScore: Math.round(rankingScore),
  
};
};
